/**
 * Gallery.jsx — Workshop Photo Gallery
 *
 * Full-page view for a single session type (Hand Building or Wheel Throwing).
 * Shows a technique description, a carousel-style photo viewer (prev/next
 * arrows, dot indicators, branded placeholder tiles for future photos), and
 * a bottom CTA that returns the user to the Classes & Workshops section.
 *
 * Props:
 *   sel           {Object}   — selected class from CLASSES (data/classes.js)
 *   onReset       {Function} — returns to the browse homepage (used by ← Back)
 *   onViewClasses {Function} — returns to browse and scrolls to ClassGrid
 */
import { useState } from 'react';
import { useStudioTilesContent } from '../services/firestore';

const CARD_ACCENT = {
  1: '#4A6FA5', // Hand Building — cobalt blue (matches carousel button)
  2: '#E8A020', // Wheel Throwing — amber (matches carousel button)
};

const TILE_KEY = { 1: 'handBuilding', 2: 'wheelThrowing' };

export default function Gallery({ sel, onReset, onViewClasses }) {
  const { tiles } = useStudioTilesContent();
  const tileData = tiles[TILE_KEY[sel.id]] || {};

  const accent = CARD_ACCENT[sel.id] || '#9C7A3C';
  const photos =
    tileData.photos && tileData.photos.length > 0
      ? tileData.photos
      : (sel.photos ?? []);
  const count = photos.length;
  const [idx, setIdx] = useState(0);

  const prev = () => setIdx((i) => (i - 1 + count) % count);
  const next = () => setIdx((i) => (i + 1) % count);

  const photo = photos[idx] ?? { url: '', caption: '' };

  return (
    <div className="section-pad" style={{ paddingTop: 32, paddingBottom: 48 }}>
      {/* ── Header tile — session name, technique description, back link ── */}
      <div
        className="tile"
        style={{
          padding: '40px 48px 32px',
          marginBottom: 24,
          position: 'relative',
          border: `4px solid ${accent}`,
        }}
      >
        {/* Decorative corner ornaments */}
        <div
          style={{
            position: 'absolute',
            top: 14,
            left: 20,
            color: '#ccc',
            fontSize: '0.65rem',
            letterSpacing: 4,
          }}
        >
          ◦ ◦
        </div>
        <div
          style={{
            position: 'absolute',
            top: 14,
            right: 20,
            color: '#ccc',
            fontSize: '0.65rem',
            letterSpacing: 4,
          }}
        >
          ◦ ◦
        </div>

        {/* Back navigation */}
        <button
          onClick={onReset}
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#9C7A3C',
            marginBottom: 20,
            display: 'block',
          }}
        >
          ← Back
        </button>

        {/* Session identity */}
        <div className="caption" style={{ color: '#9C7A3C', marginBottom: 8 }}>
          {tileData.tag || sel.tag}
        </div>
        <h2
          style={{
            fontFamily: "'Titan One', sans-serif",
            fontWeight: 400,
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            lineHeight: 0.95,
            letterSpacing: '-0.01em',
            color: '#1a1208',
            marginBottom: 20,
          }}
        >
          {(
            tileData.carouselTitle ||
            sel.carouselTitle ||
            sel.title
          ).toUpperCase()}
        </h2>

        {/* Technique description */}
        <p
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            fontSize: 'clamp(1rem, 1.8vw, 1.1rem)',
            color: '#1a1208',
            lineHeight: 1.65,
            maxWidth: 620,
          }}
        >
          {tileData.galleryDesc || sel.galleryDesc}
        </p>
      </div>

      {/* ── Photo carousel tile ──────────────────────────────────────────── */}
      {count > 0 && (
        <div
          className="tile"
          style={{
            marginBottom: 24,
            overflow: 'hidden',
            position: 'relative',
            border: `1px solid ${accent}`,
          }}
        >
          {/* ── Main photo frame ── */}
          <div
            style={{
              position: 'relative',
              aspectRatio: '1 / 1',
              background: '#F9F2E9',
              overflow: 'hidden',
            }}
          >
            {photo.url ? (
              /* Real photo */
              <img
                key={photo.url + idx}
                src={photo.url}
                alt={photo.caption}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center center',
                  display: 'block',
                  transition: 'opacity 0.3s ease',
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              /* Placeholder tile */
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 14,
                }}
              >
                {/* Corner ornaments inside placeholder */}
                {[
                  { top: 20, left: 24 },
                  { top: 20, right: 24 },
                  { bottom: 20, left: 24 },
                  { bottom: 20, right: 24 },
                ].map((pos, k) => (
                  <svg
                    key={k}
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    style={{ position: 'absolute', opacity: 0.25, ...pos }}
                  >
                    <line
                      x1="0"
                      y1="0"
                      x2="14"
                      y2="0"
                      stroke="#B8A060"
                      strokeWidth="1"
                    />
                    <line
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="14"
                      stroke="#B8A060"
                      strokeWidth="1"
                    />
                  </svg>
                ))}
                <div
                  style={{
                    fontFamily: "'Raleway', sans-serif",
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    letterSpacing: '0.2em',
                    color: '#ccc',
                  }}
                >
                  Photo Coming Soon
                </div>
                <div style={{ width: 32, height: 1, background: '#DDD8CC' }} />
                <div
                  style={{
                    fontSize: '0.78rem',
                    color: '#bbb',
                    fontStyle: 'italic',
                  }}
                >
                  {photo.caption}
                </div>
              </div>
            )}

            {/* Prev arrow */}
            <button
              className="nav-circle"
              onClick={prev}
              aria-label="Previous photo"
              style={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                background: 'rgba(249,242,233,0.85)',
              }}
            >
              ‹
            </button>

            {/* Next arrow */}
            <button
              className="nav-circle"
              onClick={next}
              aria-label="Next photo"
              style={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                background: 'rgba(249,242,233,0.85)',
              }}
            >
              ›
            </button>

            {/* Dot indicators — overlaid on photo, bottom-center */}
            {count > 1 && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 14,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 7,
                  zIndex: 2,
                }}
              >
                {photos.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    className={
                      i === idx
                        ? 'carousel-dot-photo active'
                        : 'carousel-dot-photo'
                    }
                    aria-label={`Photo ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Caption bar ── */}
          {photo.caption && (
            <div
              style={{
                padding: '14px 24px',
                borderTop: '1px solid #DDD8CC',
              }}
            >
              <div
                className="caption"
                style={{
                  fontFamily: "'Raleway', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  color: '#9C7A3C',
                  letterSpacing: '0.15em',
                }}
              >
                {photo.caption}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Bottom CTA tile — links back to Classes & Workshops ─────────── */}
      <div
        className="tile"
        style={{
          padding: '36px 48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
          position: 'relative',
          zIndex: 51,
          isolation: 'isolate',
          border: 'none',
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontWeight: 700,
              fontSize: '1rem',
              color: '#1a1208',
              marginBottom: 4,
            }}
          >
            {tileData.ctaHeading || 'Ready to get your hands dirty?'}
          </div>
          <div
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.1em',
              color: '#9C7A3C',
            }}
          >
            {tileData.ctaLocation || sel.location} ·{' '}
            {tileData.ctaDuration || sel.duration}
          </div>
        </div>
        <button className="thin-btn" onClick={onViewClasses}>
          {tileData.ctaButtonText || 'Reserve a Spot'}
        </button>
      </div>
    </div>
  );
}
